import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Heart, 
  Crown,
  Plus,
  Trash2,
  ExternalLink
} from 'lucide-react';

export default function CreatorDashboardPage() {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [receivedTips, setReceivedTips] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTier, setShowCreateTier] = useState(false);
  
  // New tier form
  const [newTierName, setNewTierName] = useState('');
  const [newTierPrice, setNewTierPrice] = useState('');
  const [newTierDescription, setNewTierDescription] = useState('');
  const [newTierBenefits, setNewTierBenefits] = useState('');
  const [creatingTier, setCreatingTier] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [earningsRes, tiersRes, tipsRes, subsRes] = await Promise.all([
          api.get('/creator/earnings'),
          api.get(`/subscriptions/tiers/${user.id}`),
          api.get('/tips/received?limit=10'),
          api.get('/subscriptions/subscribers')
        ]);
        
        setEarnings(earningsRes.data);
        setTiers(tiersRes.data);
        setReceivedTips(tipsRes.data);
        setSubscribers(subsRes.data);
      } catch (error) {
        console.error('Error fetching creator data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user.id]);

  const handleCreateTier = async () => {
    if (!newTierName || !newTierPrice || !newTierDescription) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingTier(true);
    try {
      const benefits = newTierBenefits.split('\n').filter(b => b.trim());
      const response = await api.post('/subscriptions/tiers', {
        name: newTierName,
        price: parseFloat(newTierPrice),
        description: newTierDescription,
        benefits
      });
      
      setTiers(prev => [...prev, response.data]);
      setShowCreateTier(false);
      setNewTierName('');
      setNewTierPrice('');
      setNewTierDescription('');
      setNewTierBenefits('');
      toast.success('Subscription tier created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tier');
    } finally {
      setCreatingTier(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Delete this subscription tier?')) return;
    
    try {
      await api.delete(`/subscriptions/tiers/${tierId}`);
      setTiers(prev => prev.filter(t => t.id !== tierId));
      toast.success('Tier deleted');
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  if (loading) {
    return (
      <div className="mg-page max-w-4xl py-6" data-testid="creator-dashboard">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mg-page max-w-4xl py-6" data-testid="creator-dashboard">
      <h1 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-primary" />
        Creator Dashboard
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-4 text-center">
          <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-2xl font-bold">${earnings?.total_earnings?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-muted-foreground">Total Earnings</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
          <p className="text-2xl font-bold">{earnings?.subscriber_count || 0}</p>
          <p className="text-sm text-muted-foreground">Subscribers</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Heart className="w-8 h-8 mx-auto mb-2 text-red-500" />
          <p className="text-2xl font-bold">${earnings?.tips?.total?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-muted-foreground">Tips ({earnings?.tips?.count || 0})</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Crown className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
          <p className="text-2xl font-bold">${earnings?.subscriptions?.total?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-muted-foreground">Subscriptions</p>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Subscription Tiers</h2>
          {tiers.length < 3 && (
            <Button 
              onClick={() => setShowCreateTier(true)}
              className="rounded-full"
              data-testid="create-tier-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tier
            </Button>
          )}
        </div>
        
        {tiers.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Crown className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              Create subscription tiers to let fans support you monthly
            </p>
            <Button onClick={() => setShowCreateTier(true)} className="rounded-full">
              Create Your First Tier
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map(tier => (
              <div key={tier.id} className="glass-card p-6 relative">
                <button
                  onClick={() => handleDeleteTier(tier.id)}
                  className="absolute top-4 right-4 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <h3 className="font-heading font-bold text-lg mb-2">{tier.name}</h3>
                <p className="text-3xl font-bold text-primary mb-2">
                  ${tier.price}<span className="text-sm text-muted-foreground">/month</span>
                </p>
                <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
                {tier.benefits?.length > 0 && (
                  <ul className="space-y-1">
                    {tier.benefits.map((benefit, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <span className="text-primary">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Tips */}
      <div className="mb-8">
        <h2 className="font-heading text-xl font-bold mb-4">Recent Tips</h2>
        {receivedTips.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No tips received yet</p>
        ) : (
          <div className="space-y-2">
            {receivedTips.map(tip => (
              <div key={tip.id} className="glass-card p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src={tip.from_user?.avatar}
                    alt=""
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="font-semibold">{tip.from_user?.display_name}</p>
                    {tip.message && <p className="text-sm text-muted-foreground">"{tip.message}"</p>}
                  </div>
                </div>
                <p className="font-bold text-green-500">+${tip.amount.toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subscribers */}
      <div>
        <h2 className="font-heading text-xl font-bold mb-4">Active Subscribers</h2>
        {subscribers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No active subscribers yet</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {subscribers.map(sub => (
              <div key={sub.id} className="glass-card p-4 flex items-center gap-3">
                <img 
                  src={sub.subscriber?.avatar}
                  alt=""
                  className="w-12 h-12 rounded-full"
                />
                <div className="flex-1">
                  <p className="font-semibold">{sub.subscriber?.display_name}</p>
                  <p className="text-sm text-muted-foreground">{sub.tier?.name}</p>
                </div>
                <p className="text-sm text-primary">${sub.tier?.price}/mo</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Tier Modal */}
      <Dialog open={showCreateTier} onOpenChange={setShowCreateTier}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Subscription Tier</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Tier Name</Label>
              <Input
                placeholder="e.g., Bronze, Silver, Gold"
                value={newTierName}
                onChange={(e) => setNewTierName(e.target.value)}
                className="mt-1"
                data-testid="tier-name-input"
              />
            </div>
            
            <div>
              <Label>Monthly Price ($)</Label>
              <Input
                type="number"
                placeholder="9.99"
                min="1"
                max="100"
                step="0.01"
                value={newTierPrice}
                onChange={(e) => setNewTierPrice(e.target.value)}
                className="mt-1"
                data-testid="tier-price-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="What do subscribers get?"
                value={newTierDescription}
                onChange={(e) => setNewTierDescription(e.target.value)}
                className="mt-1"
                rows={2}
                data-testid="tier-description-input"
              />
            </div>
            
            <div>
              <Label>Benefits (one per line)</Label>
              <Textarea
                placeholder="Exclusive posts&#10;Early access&#10;Direct messages"
                value={newTierBenefits}
                onChange={(e) => setNewTierBenefits(e.target.value)}
                className="mt-1"
                rows={3}
                data-testid="tier-benefits-input"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreateTier(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTier}
                disabled={creatingTier}
                data-testid="submit-tier-btn"
              >
                {creatingTier ? 'Creating...' : 'Create Tier'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
